import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Download, FileText, Filter, RefreshCw, AlertTriangle, CheckCircle2,
  Plus, Trash2, DollarSign, Calendar,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

// 2026-05-20 (Angela) — Additive Finance module. Reads check_payments +
// daily_closings; writes only to inspector_pay_rates + day_rate_overrides.
// HARD LOCK: must NOT modify existing Finance / Check Reconcile / Productivity.

const fmt$ = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const mondayOf = (d) => {
  const dt = new Date(d);
  const day = dt.getDay();
  const diff = (day === 0 ? -6 : 1 - day);
  dt.setDate(dt.getDate() + diff);
  return dt.toISOString().slice(0, 10);
};
const sundayOf = (d) => {
  const dt = new Date(mondayOf(d));
  dt.setDate(dt.getDate() + 6);
  return dt.toISOString().slice(0, 10);
};

const InspectorPayrollReportModule = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(mondayOf(today));
  const [toDate, setToDate] = useState(sundayOf(today));
  const [checkBatchId, setCheckBatchId] = useState("");
  const [inspectorCode, setInspectorCode] = useState("");
  const [batches, setBatches] = useState([]);
  const [inspectors, setInspectors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [activeView, setActiveView] = useState("report"); // report | extract | rates | overrides
  const [error, setError] = useState("");

  const loadLookups = useCallback(async () => {
    try {
      const [b, i] = await Promise.all([
        axios.get(`${API}/finance/payroll/check-batches?limit=50`),
        axios.get(`${API}/finance/payroll/inspectors`),
      ]);
      setBatches(b.data?.batches || []);
      setInspectors(i.data?.inspectors || []);
    } catch (e) {
      /* silent */
    }
  }, []);

  useEffect(() => { loadLookups(); }, [loadLookups]);

  const runReport = async () => {
    setLoading(true);
    setError("");
    setReport(null);
    try {
      const params = new URLSearchParams({ from_date: fromDate, to_date: toDate });
      if (checkBatchId) params.append("check_batch_id", checkBatchId);
      if (inspectorCode) params.append("inspector_code", inspectorCode);
      const r = await axios.get(`${API}/finance/payroll/report?${params.toString()}`);
      setReport(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    const params = new URLSearchParams({ from_date: fromDate, to_date: toDate });
    if (checkBatchId) params.append("check_batch_id", checkBatchId);
    if (inspectorCode) params.append("inspector_code", inspectorCode);
    window.open(`${API}/finance/payroll/report.csv?${params.toString()}`, "_blank");
  };

  const exportPdf = () => {
    const params = new URLSearchParams({ from_date: fromDate, to_date: toDate });
    if (checkBatchId) params.append("check_batch_id", checkBatchId);
    if (inspectorCode) params.append("inspector_code", inspectorCode);
    window.open(`${API}/finance/payroll/report.pdf?${params.toString()}`, "_blank");
  };

  return (
    <div className="space-y-4" data-testid="payroll-report-module">
      <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 pb-2">
        {[
          { key: "report", label: "Report", icon: FileText },
          { key: "extract", label: "Extract (Read-Only)", icon: Calendar },
          { key: "rates", label: "Pay Rates", icon: DollarSign },
          { key: "overrides", label: "Day Overrides", icon: Calendar },
        ].map((t) => {
          const Icon = t.icon;
          const active = activeView === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setActiveView(t.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition ${
                active ? "bg-[#162d4a] text-white" : "text-gray-600 hover:bg-gray-100"
              }`}
              data-testid={`payroll-subtab-${t.key}`}
            >
              <Icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {activeView === "report" && (
        <ReportView
          fromDate={fromDate} setFromDate={setFromDate}
          toDate={toDate} setToDate={setToDate}
          checkBatchId={checkBatchId} setCheckBatchId={setCheckBatchId}
          inspectorCode={inspectorCode} setInspectorCode={setInspectorCode}
          batches={batches} inspectors={inspectors}
          loading={loading} error={error}
          report={report} runReport={runReport}
          exportCsv={exportCsv} exportPdf={exportPdf}
        />
      )}

      {activeView === "rates" && <RatesEditor inspectors={inspectors} />}
      {activeView === "overrides" && <OverridesEditor inspectors={inspectors} />}
      {activeView === "extract" && <ExtractView />}
    </div>
  );
};


/* ────────────────── EXTRACT (READ-ONLY / SOURCE OF TRUTH) ────────────────── */

const ExtractView = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(mondayOf(today));
  const [toDate, setToDate] = useState(sundayOf(today));
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const run = async () => {
    setLoading(true); setError(""); setData(null);
    try {
      const r = await axios.get(`${API}/finance/payroll/extract`, {
        params: { from_date: fromDate, to_date: toDate },
      });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to extract");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-4" data-testid="payroll-extract-view">
      <div className="bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg text-xs text-amber-900" data-testid="extract-readonly-banner">
        <b>READ-ONLY · SOURCE OF TRUTH.</b> This view does NOT calculate anything.
        It exposes exactly what the system already stores: <code>daily_closings.payment_amount</code> per order
        (=TOTAL PAY), the published pay-rate card on <code>team_members</code>, plus any
        <code> inspector_pay_rates</code> and <code>day_rate_overrides</code> active in the window.
      </div>

      <div className="bg-white p-3 rounded-xl border border-gray-200 flex flex-wrap items-end gap-2">
        <div>
          <label className="text-[10px] uppercase text-gray-400 font-bold block mb-1">From</label>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="px-2 py-1.5 border border-gray-200 rounded text-sm" data-testid="extract-from" />
        </div>
        <div>
          <label className="text-[10px] uppercase text-gray-400 font-bold block mb-1">To</label>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="px-2 py-1.5 border border-gray-200 rounded text-sm" data-testid="extract-to" />
        </div>
        <button onClick={run} disabled={loading} className="bg-[#162d4a] text-white text-sm font-semibold py-2 px-3 rounded-lg hover:bg-[#1F4068] disabled:opacity-50" data-testid="extract-run">
          {loading ? "Reading..." : "Extract"}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">{error}</div>
      )}

      {data && (
        <>
          <div className="bg-white p-3 rounded-xl border border-gray-200" data-testid="extract-rules">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Rules Read From (verbatim, system-stored)</div>
            <ul className="text-xs text-gray-700 list-disc list-inside space-y-0.5">
              {data.rules_read_from.map((r, i) => <li key={i}><code className="text-[11px] bg-gray-100 px-1 py-0.5 rounded">{r}</code></li>)}
            </ul>
            <div className="mt-2 text-[11px] text-gray-500">
              Window: {data.parameters.from_date} → {data.parameters.to_date} ({data.parameters.timezone})
              &nbsp;·&nbsp; Inspectors: {data.grand_totals.inspectors}
              &nbsp;·&nbsp; Total orders: {data.grand_totals.total_orders}
              &nbsp;·&nbsp; System total pay (sum of daily_closings.payment_amount): <b>{fmt$(data.grand_totals.system_total_pay)}</b>
              {data.grand_totals.closings_missing_payment_amount > 0 && (
                <span className="ml-1 text-amber-700 font-semibold">
                  ⚠ {data.grand_totals.closings_missing_payment_amount} closings have payment_amount=0 in the system
                </span>
              )}
            </div>
          </div>

          {data.inspectors.length === 0 && (
            <div className="bg-white p-6 rounded-xl border border-gray-200 text-center text-sm text-gray-500">
              No closings in this window.
            </div>
          )}

          {data.inspectors.map(b => (
            <div key={b.inspector_code} className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid={`extract-card-${b.inspector_code}`}>
              <div className="px-4 py-2 bg-[#162d4a] text-white flex items-center justify-between">
                <div>
                  <div className="text-sm font-bold">{b.inspector_code}</div>
                  <div className="text-[11px] opacity-80">{b.inspector_name}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider opacity-70">System Total Pay</div>
                  <div className="text-xl font-extrabold">{fmt$(b.system_total_pay)}</div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-200">
                <div className="p-3 text-xs">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Production</div>
                  <div>Total orders: <b>{b.total_orders}</b></div>
                  <div>Internal: <b>{b.by_flow.internal}</b></div>
                  <div>External: <b>{b.by_flow.external}</b></div>
                  <div>Regular: <b>{b.by_flow.regular}</b></div>
                  {b.by_flow.other > 0 && <div className="text-amber-700">Other / blank flow_type: <b>{b.by_flow.other}</b></div>}
                  {b.missing_payment_amount > 0 && (
                    <div className="text-amber-700 mt-1">
                      ⚠ {b.missing_payment_amount} closings with payment_amount=0 stored
                    </div>
                  )}
                </div>
                <div className="p-3 text-xs">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Pay Structure (system-stored)</div>
                  {b.pay_structure.team_members ? (
                    <div>
                      <div className="text-[10px] text-gray-500"><code>team_members</code></div>
                      <div>Internal: <b>{fmt$(b.pay_structure.team_members.internal_rate)}</b></div>
                      <div>External: <b>{fmt$(b.pay_structure.team_members.external_rate)}</b></div>
                      <div>Regular: <b>{fmt$(b.pay_structure.team_members.regular_rate)}</b></div>
                    </div>
                  ) : <div className="text-gray-400">No team_members rate stored</div>}
                  {b.pay_structure.inspector_pay_rates.length > 0 && (
                    <div className="mt-2">
                      <div className="text-[10px] text-gray-500"><code>inspector_pay_rates</code></div>
                      {b.pay_structure.inspector_pay_rates.map((r, i) => (
                        <div key={i}>{r.flow_type}{r.work_code ? `/${r.work_code}` : ""} · {r.rate_type} · <b>{fmt$(r.rate_amount)}</b> <span className="text-gray-400">({r.effective_from}→{r.effective_to || "open"})</span></div>
                      ))}
                    </div>
                  )}
                  {b.pay_structure.day_rate_overrides.length > 0 && (
                    <div className="mt-2">
                      <div className="text-[10px] text-gray-500"><code>day_rate_overrides</code></div>
                      {b.pay_structure.day_rate_overrides.map((o, i) => (
                        <div key={i}>{o.route_date}: <b>{fmt$(o.amount)}</b> {o.reason && <span className="text-gray-400">— {o.reason}</span>}</div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="p-3 text-xs">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Sample Orders (first 5)</div>
                  <table className="min-w-full text-[11px]">
                    <tbody>
                      {b.sample_orders.map((s, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          <td className="py-1 pr-1 font-mono">{s.order_number}</td>
                          <td className="py-1 pr-1 text-gray-500">{s.flow_type || "—"}</td>
                          <td className="py-1 pr-1 text-gray-500">{s.route_date}</td>
                          <td className="py-1 pr-1 text-right font-semibold">{fmt$(s.payment_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="text-[10px] text-gray-400 mt-1">field: <code>daily_closings.payment_amount</code></div>
                </div>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
};


/* ────────────────── REPORT VIEW ────────────────── */

const ReportView = ({
  fromDate, setFromDate, toDate, setToDate,
  checkBatchId, setCheckBatchId, inspectorCode, setInspectorCode,
  batches, inspectors, loading, error, report, runReport, exportCsv, exportPdf,
}) => {
  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white p-4 rounded-xl border border-gray-200" data-testid="payroll-filters">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
          <div>
            <label className="text-[10px] uppercase text-gray-400 font-bold block mb-1">From</label>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm" data-testid="payroll-from-date" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-gray-400 font-bold block mb-1">To</label>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm" data-testid="payroll-to-date" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-gray-400 font-bold block mb-1">Check Batch (optional)</label>
            <select value={checkBatchId} onChange={e => setCheckBatchId(e.target.value)} className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm" data-testid="payroll-batch-select">
              <option value="">All check batches in range</option>
              {batches.map(b => (
                <option key={b.batch_id} value={b.batch_id}>
                  {b.batch_id} — {b.total_rows} rows · {fmt$(b.total_amount)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase text-gray-400 font-bold block mb-1">Inspector (optional)</label>
            <select value={inspectorCode} onChange={e => setInspectorCode(e.target.value)} className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm" data-testid="payroll-inspector-select">
              <option value="">All inspectors</option>
              {inspectors.map(i => (
                <option key={i.inspector_code} value={i.inspector_code}>
                  {i.inspector_name} ({i.inspector_code})
                </option>
              ))}
            </select>
          </div>
          <button onClick={runReport} disabled={loading} className="bg-[#162d4a] text-white text-sm font-semibold py-2 px-3 rounded-lg hover:bg-[#1F4068] disabled:opacity-50 flex items-center gap-1.5" data-testid="payroll-run-btn">
            <Filter className="h-3.5 w-3.5" /> {loading ? "Running..." : "Run Report"}
          </button>
          <div className="flex gap-1.5">
            <button onClick={exportCsv} disabled={!report} className="flex-1 bg-white border border-gray-200 text-gray-700 text-xs font-semibold py-2 px-2 rounded-lg hover:bg-gray-50 disabled:opacity-40 flex items-center justify-center gap-1" data-testid="payroll-csv-btn">
              <Download className="h-3 w-3" /> CSV
            </button>
            <button onClick={exportPdf} disabled={!report} className="flex-1 bg-[#D1A054] text-[#162d4a] text-xs font-semibold py-2 px-2 rounded-lg hover:bg-[#B8893E] disabled:opacity-40 flex items-center justify-center gap-1" data-testid="payroll-pdf-btn">
              <Download className="h-3 w-3" /> PDF
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm" data-testid="payroll-error">
          {error}
        </div>
      )}

      {report && (
        <>
          {/* Reconciliation banner */}
          <ReconciliationBanner rec={report.reconciliation} discrepancies={report.discrepancies} />

          {/* Final totals */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="payroll-totals">
            <TotalCard label="Paid Orders" value={report.totals.paid_orders} accent="navy" />
            <TotalCard label="Revenue (SG)" value={fmt$(report.totals.revenue)} accent="navy" />
            <TotalCard label="Inspector Payment" value={fmt$(report.totals.payment)} accent="gold" />
            <TotalCard label="AARIFI Profit" value={fmt$(report.totals.profit)} accent={report.totals.profit >= 0 ? "green" : "red"} />
            <TotalCard label="Unpaid (Not in totals)" value={report.totals.unpaid_orders} accent="red" />
          </div>

          {/* Inspector summary */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="payroll-inspector-summary">
            <div className="px-4 py-2 bg-[#162d4a] text-white text-xs font-bold uppercase tracking-wider">Per-Inspector Summary</div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Inspector</th>
                    <th className="px-3 py-2 text-right">Paid Orders</th>
                    <th className="px-3 py-2 text-right">Revenue</th>
                    <th className="px-3 py-2 text-right">Payment</th>
                    <th className="px-3 py-2 text-right">Profit</th>
                    <th className="px-3 py-2 text-right">Internal</th>
                    <th className="px-3 py-2 text-right">External</th>
                    <th className="px-3 py-2 text-right">Regular</th>
                  </tr>
                </thead>
                <tbody>
                  {report.inspector_summary.map(s => (
                    <tr key={s.inspector_code} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`payroll-row-${s.inspector_code}`}>
                      <td className="px-3 py-2 font-semibold text-gray-800">{s.inspector_code}</td>
                      <td className="px-3 py-2 text-right">{s.paid_orders}</td>
                      <td className="px-3 py-2 text-right">{fmt$(s.revenue)}</td>
                      <td className="px-3 py-2 text-right">{fmt$(s.payment)}</td>
                      <td className={`px-3 py-2 text-right font-bold ${s.profit < 0 ? "text-red-600" : "text-emerald-700"}`}>{fmt$(s.profit)}</td>
                      <td className="px-3 py-2 text-right text-gray-500">{s.by_flow.internal.orders} · {fmt$(s.by_flow.internal.profit)}</td>
                      <td className="px-3 py-2 text-right text-gray-500">{s.by_flow.external.orders} · {fmt$(s.by_flow.external.profit)}</td>
                      <td className="px-3 py-2 text-right text-gray-500">{s.by_flow.regular.orders} · {fmt$(s.by_flow.regular.profit)}</td>
                    </tr>
                  ))}
                  {report.inspector_summary.length === 0 && (
                    <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-400">No paid orders in this period</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Paid orders detail (first 100) */}
          <DetailTable
            title={`Paid Orders Detail (showing first 100 of ${report.paid_orders.length})`}
            rows={report.paid_orders.slice(0, 100)}
            testid="payroll-paid-detail"
            kind="paid"
          />

          {/* Unpaid */}
          {report.unpaid_orders.length > 0 && (
            <DetailTable
              title={`Unpaid / To-Claim Orders (${report.unpaid_orders.length}) — NOT included in totals`}
              rows={report.unpaid_orders.slice(0, 100)}
              testid="payroll-unpaid-detail"
              kind="unpaid"
            />
          )}
        </>
      )}
    </div>
  );
};


const TotalCard = ({ label, value, accent }) => {
  const accents = {
    navy: "bg-[#162d4a] text-white",
    gold: "bg-[#D1A054] text-[#162d4a]",
    green: "bg-emerald-600 text-white",
    red: "bg-[#B5302A] text-white",
  };
  return (
    <div className={`${accents[accent] || accents.navy} px-4 py-3 rounded-xl`} data-testid={`payroll-card-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">{label}</div>
      <div className="text-2xl font-extrabold mt-1">{value}</div>
    </div>
  );
};


const ReconciliationBanner = ({ rec, discrepancies }) => {
  const balanced = rec.balanced;
  return (
    <div
      className={`px-4 py-3 rounded-xl border-2 ${
        balanced
          ? "bg-emerald-50 border-emerald-300 text-emerald-800"
          : "bg-red-50 border-red-300 text-red-800"
      }`}
      data-testid="payroll-reconciliation-banner"
    >
      <div className="flex items-start gap-2">
        {balanced ? <CheckCircle2 className="h-4 w-4 mt-0.5" /> : <AlertTriangle className="h-4 w-4 mt-0.5" />}
        <div className="flex-1">
          <div className="text-xs font-extrabold uppercase tracking-wider">
            {balanced ? "Reconciled" : "Reconciliation Mismatch"}
          </div>
          <div className="text-xs mt-1">
            Safeguard Check Total: <b>{fmt$(rec.check_payments_total)}</b> &nbsp;·&nbsp;
            Report Revenue: <b>{fmt$(rec.report_revenue_total)}</b> &nbsp;·&nbsp;
            Δ: <b>{fmt$(rec.difference)}</b>
          </div>
          {!balanced && (
            <div className="text-xs mt-1">
              {rec.orders_in_check_not_in_closings.length} check rows have no matching daily_closing in the window.
              Showing only orders that exist in BOTH check_payments AND daily_closings.
            </div>
          )}
          {discrepancies.length > 0 && (
            <details className="mt-1.5">
              <summary className="cursor-pointer text-[11px] font-semibold underline">
                {discrepancies.length} discrepancy item{discrepancies.length === 1 ? "" : "s"}
              </summary>
              <ul className="mt-1 text-[11px] space-y-0.5">
                {discrepancies.slice(0, 10).map((d, i) => (
                  <li key={i}>• <b>{d.type}</b>{d.order_number ? ` · order ${d.order_number}` : ""} — {d.detail}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </div>
    </div>
  );
};


const DetailTable = ({ title, rows, testid, kind }) => (
  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid={testid}>
    <div className={`px-4 py-2 ${kind === "unpaid" ? "bg-[#B5302A]" : "bg-[#162d4a]"} text-white text-xs font-bold uppercase tracking-wider`}>
      {title}
    </div>
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs">
        <thead className="bg-gray-50 text-gray-600">
          {kind === "paid" ? (
            <tr>
              <th className="px-3 py-2 text-left">Order #</th>
              <th className="px-3 py-2 text-left">Inspector</th>
              <th className="px-3 py-2 text-left">Flow</th>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-right">SG Received</th>
              <th className="px-3 py-2 text-left">Rate Source</th>
              <th className="px-3 py-2 text-right">Rate</th>
              <th className="px-3 py-2 text-right">Payment</th>
              <th className="px-3 py-2 text-right">Profit</th>
              <th className="px-3 py-2 text-center">Override</th>
            </tr>
          ) : (
            <tr>
              <th className="px-3 py-2 text-left">Order #</th>
              <th className="px-3 py-2 text-left">Inspector</th>
              <th className="px-3 py-2 text-left">Flow</th>
              <th className="px-3 py-2 text-left">Route Date</th>
              <th className="px-3 py-2 text-right">AARIFI Amount</th>
              <th className="px-3 py-2 text-left">Reason</th>
            </tr>
          )}
        </thead>
        <tbody>
          {rows.map((r, idx) => kind === "paid" ? (
            <tr key={`${r.order_number}-${idx}`} className="border-t border-gray-100 hover:bg-gray-50">
              <td className="px-3 py-1.5 font-mono text-[11px]">{r.order_number}</td>
              <td className="px-3 py-1.5">{r.inspector_code}</td>
              <td className="px-3 py-1.5">{r.flow_type}</td>
              <td className="px-3 py-1.5">{r.route_date}</td>
              <td className="px-3 py-1.5 text-right">{fmt$(r.safeguard_amount_received)}</td>
              <td className="px-3 py-1.5"><span className={`text-[10px] px-1.5 py-0.5 rounded ${r.rate_source === "pay_rate" ? "bg-emerald-100 text-emerald-800" : r.rate_source === "team_members" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"}`}>{r.rate_source}</span></td>
              <td className="px-3 py-1.5 text-right">{fmt$(r.rate_amount)}</td>
              <td className="px-3 py-1.5 text-right">{fmt$(r.inspector_payment)}</td>
              <td className={`px-3 py-1.5 text-right font-semibold ${r.aarifi_profit < 0 ? "text-red-600" : "text-emerald-700"}`}>{fmt$(r.aarifi_profit)}</td>
              <td className="px-3 py-1.5 text-center">{r.override_applied ? "✓" : ""}</td>
            </tr>
          ) : (
            <tr key={`${r.order_number}-${idx}`} className="border-t border-gray-100 hover:bg-gray-50">
              <td className="px-3 py-1.5 font-mono text-[11px]">{r.order_number}</td>
              <td className="px-3 py-1.5">{r.inspector_code}</td>
              <td className="px-3 py-1.5">{r.flow_type}</td>
              <td className="px-3 py-1.5">{r.route_date}</td>
              <td className="px-3 py-1.5 text-right">{fmt$(r.aarifi_amount)}</td>
              <td className="px-3 py-1.5 text-gray-500">{r.reason}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);


/* ────────────────── PAY RATES EDITOR ────────────────── */

const RatesEditor = ({ inspectors }) => {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    inspector_code: "", flow_type: "internal", work_code: "",
    rate_type: "per_order", rate_amount: "",
    effective_from: new Date().toISOString().slice(0, 10),
    effective_to: "", active: true,
  });
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/finance/payroll/rates?active_only=false`);
      setRates(r.data?.rates || []);
    } catch (e) { /* silent */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e?.preventDefault();
    setMsg(null);
    try {
      const payload = { ...form };
      payload.rate_amount = Number(payload.rate_amount);
      if (!payload.effective_to) delete payload.effective_to;
      if (!payload.work_code) delete payload.work_code;
      await axios.post(`${API}/finance/payroll/rates`, payload);
      setMsg({ type: "ok", text: "Rate saved" });
      setForm({ ...form, rate_amount: "" });
      load();
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || "Failed to save" });
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Deactivate this rate?")) return;
    try {
      await axios.delete(`${API}/finance/payroll/rates/${id}`);
      load();
    } catch (e) { /* silent */ }
  };

  return (
    <div className="space-y-3" data-testid="payroll-rates-editor">
      <form onSubmit={submit} className="bg-white p-4 rounded-xl border border-gray-200">
        <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> Add / Update Pay Rate</h3>
        <div className="grid grid-cols-2 md:grid-cols-7 gap-2 items-end">
          <select value={form.inspector_code} onChange={e => setForm({ ...form, inspector_code: e.target.value })} required className="px-2 py-1.5 border border-gray-200 rounded text-xs col-span-2" data-testid="rate-inspector">
            <option value="">Select Inspector</option>
            {inspectors.map(i => <option key={i.inspector_code} value={i.inspector_code}>{i.inspector_code}</option>)}
          </select>
          <select value={form.flow_type} onChange={e => setForm({ ...form, flow_type: e.target.value })} className="px-2 py-1.5 border border-gray-200 rounded text-xs" data-testid="rate-flow">
            <option value="internal">internal</option>
            <option value="external">external</option>
            <option value="regular">regular</option>
          </select>
          <input value={form.work_code} onChange={e => setForm({ ...form, work_code: e.target.value })} placeholder="Work code (opt)" className="px-2 py-1.5 border border-gray-200 rounded text-xs" data-testid="rate-workcode" />
          <select value={form.rate_type} onChange={e => setForm({ ...form, rate_type: e.target.value })} className="px-2 py-1.5 border border-gray-200 rounded text-xs" data-testid="rate-type">
            <option value="per_order">per_order</option>
            <option value="day_rate">day_rate</option>
          </select>
          <input type="number" step="0.01" value={form.rate_amount} onChange={e => setForm({ ...form, rate_amount: e.target.value })} placeholder="Amount $" required className="px-2 py-1.5 border border-gray-200 rounded text-xs" data-testid="rate-amount" />
          <button type="submit" className="bg-[#162d4a] text-white text-xs font-semibold py-1.5 px-3 rounded hover:bg-[#1F4068]" data-testid="rate-save">Save</button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
          <div>
            <label className="text-[9px] uppercase text-gray-400 block">Effective from</label>
            <input type="date" value={form.effective_from} onChange={e => setForm({ ...form, effective_from: e.target.value })} className="w-full px-2 py-1 border border-gray-200 rounded text-xs" />
          </div>
          <div>
            <label className="text-[9px] uppercase text-gray-400 block">Effective to (opt)</label>
            <input type="date" value={form.effective_to} onChange={e => setForm({ ...form, effective_to: e.target.value })} className="w-full px-2 py-1 border border-gray-200 rounded text-xs" />
          </div>
        </div>
        {msg && <div className={`mt-2 text-xs px-2 py-1 rounded ${msg.type === "ok" ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"}`}>{msg.text}</div>}
      </form>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 bg-gray-50">
          <h3 className="text-xs font-bold uppercase text-gray-700">All Rates ({rates.length})</h3>
          <button onClick={load} className="text-xs text-[#162d4a] flex items-center gap-1 hover:underline" data-testid="rates-refresh">
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">Inspector</th>
              <th className="px-3 py-2 text-left">Flow</th>
              <th className="px-3 py-2 text-left">Work Code</th>
              <th className="px-3 py-2 text-left">Type</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2 text-left">Effective</th>
              <th className="px-3 py-2 text-left">Active</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rates.map(r => (
              <tr key={r.rate_id} className="border-t border-gray-100">
                <td className="px-3 py-1.5 font-semibold">{r.inspector_code}</td>
                <td className="px-3 py-1.5">{r.flow_type}</td>
                <td className="px-3 py-1.5">{r.work_code || "—"}</td>
                <td className="px-3 py-1.5">{r.rate_type}</td>
                <td className="px-3 py-1.5 text-right">{fmt$(r.rate_amount)}</td>
                <td className="px-3 py-1.5">{r.effective_from}{r.effective_to ? ` → ${r.effective_to}` : " → open"}</td>
                <td className="px-3 py-1.5">{r.active ? <span className="text-emerald-600 font-bold">yes</span> : <span className="text-gray-400">no</span>}</td>
                <td className="px-3 py-1.5">
                  {r.active && (
                    <button onClick={() => remove(r.rate_id)} className="text-red-600 hover:text-red-800" data-testid={`rate-del-${r.rate_id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!loading && rates.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-4 text-center text-gray-400">No rates yet. Add the first one above.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};


/* ────────────────── DAY-RATE OVERRIDES EDITOR ────────────────── */

const OverridesEditor = ({ inspectors }) => {
  const [overrides, setOverrides] = useState([]);
  const [form, setForm] = useState({
    inspector_code: "", route_date: new Date().toISOString().slice(0, 10),
    amount: "", reason: "",
  });
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/finance/payroll/overrides`);
      setOverrides(r.data?.overrides || []);
    } catch (e) { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e?.preventDefault();
    setMsg(null);
    try {
      await axios.post(`${API}/finance/payroll/overrides`, {
        ...form, amount: Number(form.amount),
      });
      setMsg({ type: "ok", text: "Override saved" });
      setForm({ ...form, amount: "", reason: "" });
      load();
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || "Failed" });
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this override?")) return;
    try { await axios.delete(`${API}/finance/payroll/overrides/${id}`); load(); } catch (e) { /* silent */ }
  };

  return (
    <div className="space-y-3" data-testid="payroll-overrides-editor">
      <form onSubmit={submit} className="bg-white p-4 rounded-xl border border-gray-200">
        <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> Add Day-Rate Override</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
          <select value={form.inspector_code} onChange={e => setForm({ ...form, inspector_code: e.target.value })} required className="px-2 py-1.5 border border-gray-200 rounded text-xs" data-testid="ovr-inspector">
            <option value="">Select Inspector</option>
            {inspectors.map(i => <option key={i.inspector_code} value={i.inspector_code}>{i.inspector_code}</option>)}
          </select>
          <input type="date" value={form.route_date} onChange={e => setForm({ ...form, route_date: e.target.value })} required className="px-2 py-1.5 border border-gray-200 rounded text-xs" data-testid="ovr-date" />
          <input type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="Flat day $" required className="px-2 py-1.5 border border-gray-200 rounded text-xs" data-testid="ovr-amount" />
          <input value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} placeholder="Reason" className="px-2 py-1.5 border border-gray-200 rounded text-xs" data-testid="ovr-reason" />
          <button type="submit" className="bg-[#162d4a] text-white text-xs font-semibold py-1.5 px-3 rounded hover:bg-[#1F4068]" data-testid="ovr-save">Save</button>
        </div>
        {msg && <div className={`mt-2 text-xs px-2 py-1 rounded ${msg.type === "ok" ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"}`}>{msg.text}</div>}
      </form>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-2 bg-gray-50 text-xs font-bold uppercase text-gray-700">All Overrides ({overrides.length})</div>
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">Inspector</th>
              <th className="px-3 py-2 text-left">Route Date</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2 text-left">Reason</th>
              <th className="px-3 py-2 text-left">Created</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {overrides.map(o => (
              <tr key={o.override_id} className="border-t border-gray-100">
                <td className="px-3 py-1.5 font-semibold">{o.inspector_code}</td>
                <td className="px-3 py-1.5">{o.route_date}</td>
                <td className="px-3 py-1.5 text-right">{fmt$(o.amount)}</td>
                <td className="px-3 py-1.5">{o.reason || "—"}</td>
                <td className="px-3 py-1.5 text-gray-500">{(o.created_at || "").slice(0, 10)}</td>
                <td className="px-3 py-1.5">
                  <button onClick={() => remove(o.override_id)} className="text-red-600 hover:text-red-800" data-testid={`ovr-del-${o.override_id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
            {overrides.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-4 text-center text-gray-400">No overrides yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InspectorPayrollReportModule;
